import type { routes } from '@semios/app-platform-banyan-route-definitions'
import type { VV } from '@semios/app-platform-value-type-definitions'
import { blockLacksCropSectionMaker } from 'App/Map/PanelDetails/_utils/blockLacksCropSectionMaker'
import { getTimezoneForSelectedPropertyOrRegion } from 'App/Map/PanelDetails/_utils/getTimezoneForSelectedPropertyOrRegion'
import { propertyLacksPermissionSectionMaker } from 'App/Map/PanelDetails/_utils/propertyLacksPermissionSectionMaker'
import { selectedPropertyHasPermission } from 'App/Map/PanelDetails/_utils/selectedPropertyHasPermission'
import { DropdownSelectorBlock } from 'App/Map/PanelDetails/SectionTitleBars/DropdownSelectorBlock/DropdownSelectorBlock'
import type { TXAxisPlotLinesOptionsWithTooltipDisplay } from 'components/StackedChart/_utils/lineChartTooltipFormatter/buildTooltipPlotLinesIfAny'
import type {
  StackedChartSection,
  TChartSeries,
  TChartSeriesWithTRGBAColorWith1AtTheEndColor,
} from 'components/StackedChart/types'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import type { TRGBAColorWith1AtTheEnd } from 'settings/colors'
import { detailsPanelStore } from 'stores/detailsPanelStore/detailsPanelStore'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type {
  TPointCategory,
  TSelectedFieldAssetsStoreState,
} from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import type { selectedValueGroupsStore } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { doesSelectedBlockHaveValueTypes } from 'utils/doesSelectedFieldAssetHaveValueTypes'
import { getCropNameFromId } from 'utils/getCropNameFromId'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import { PowderyMildewTooltip } from '../../../../../_utils/by-domain/powderyMildew/PowderyMildewTooltip'
import { getXDateFormat } from '../../_utils/getXDateFormat'
import { makeCompareSeasonsSeriesFromRegularSeries } from '../../_utils/makeCompareSeasonsSeriesFromRegularSeries'
import { makeDailyRainForDiseases } from '../../_utils/makeDailyRainForDiseases'

type TValueTypeKey = VV.DomainTypes.DegreeDaysCrop.TTimeseriesValueTypeKeysMerged

const preferredAggregationInterval = { preferredAggregationInterval: 'DAILY' } as const
const pointCategory: TPointCategory = 'outOfBlockPoint'
const cherryCropIds = [3, 11, 12]

const allPossibleValueTypes = cherryCropIds.map(
  (id) => `cropDegreeDaysCumulativeSinceBiofix_${id}` as TValueTypeKey,
)

const getValuesRequestedForBlocks = ({
  selectedFieldAssets,
  selectedValueGroups,
}: {
  selectedValueGroups: ReturnType<typeof selectedValueGroupsStore.getState>['selectedValueGroups']
  selectedFieldAssets: TSelectedFieldAssetsStoreState
}): Partial<routes.Values.Request> => {
  if (!selectedFieldAssets.block) return {}

  const valuesRequested: Partial<Record<TValueTypeKey, typeof preferredAggregationInterval>> = {}

  Object.entries(selectedValueGroups).forEach(([valueGroup, isActive]) => {
    if (!!isActive && valueGroup.includes('cherry_powdery_mildew')) {
      if (!selectedPropertyHasPermission({ permission: 'VIEW_CHERRY_POWDERY_MILDEW_DETAILS' })) return

      const { property } = selectedFieldAssetsStore.getState()
      const propertyObj = fieldAssetStore.getState().properties?.[Number(property)]
      const cropIdFromFieldAssets = propertyObj?.blocks?.[Number(selectedFieldAssets.block)]?.cropIds?.[0]

      valuesRequested[
        `cropDegreeDaysCumulativeSinceBiofix_${Number(cropIdFromFieldAssets)}` as TValueTypeKey
      ] = preferredAggregationInterval
    }
  })

  if (
    Object.keys(valuesRequested).length === 0 ||
    !doesSelectedBlockHaveValueTypes({ valuesTimeseries: Object.keys(valuesRequested) as TValueTypeKey[] })
  )
    return {}

  const valuesToBeRequested: Partial<routes.Values.Request> = {}

  if (selectedFieldAssets[pointCategory]) {
    valuesToBeRequested.points = {
      lngLats: [selectedFieldAssets[pointCategory]],
      valuesRequested: {
        precipitation: { preferredAggregationInterval: 'DAILY' },
      },
    }
  }

  valuesToBeRequested.blocks = {
    blockIds: [selectedFieldAssets.block],
    valuesRequested,
  }

  return valuesToBeRequested
}

export const apiArgs = ({
  selectedValueGroups,
  selectedFieldAssets,
}: {
  selectedValueGroups: ReturnType<typeof selectedValueGroupsStore.getState>['selectedValueGroups']
  selectedFieldAssets: TSelectedFieldAssetsStoreState
}) => {
  const blockValues = getValuesRequestedForBlocks({ selectedFieldAssets, selectedValueGroups })

  return { ...blockValues }
}

export const content = ({
  data,
  compareSeasonsData,
}: {
  data: routes.Values.Response
  compareSeasonsData: routes.Values.Response
}): StackedChartSection => {
  const { compareSeasonsInterval } = detailsPanelStore.getState()
  const selectedFieldAssets = selectedFieldAssetsStore.getState()
  const selectedProperty = selectedFieldAssets.property
  const blockId = String(selectedFieldAssets.block)
  const timezone = getTimezoneForSelectedPropertyOrRegion()
  const propertyObj = fieldAssetStore.getState().properties?.[Number(selectedProperty)]
  const cropIdFromFieldAssets = propertyObj?.blocks?.[Number(selectedFieldAssets.block)]?.cropIds?.[0]

  const hasPermission = selectedPropertyHasPermission({
    permission: 'VIEW_CHERRY_POWDERY_MILDEW_DETAILS',
  })

  const valueTypeKey = `cropDegreeDaysCumulativeSinceBiofix_${Number(cropIdFromFieldAssets)}` as TValueTypeKey

  const commonReturnItems = {
    title: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span>
          {translate.phrases.banyanApp('{{diseaseName}} Primary Infection Monitoring', {
            diseaseName: translate.phrases.banyanApp('Cherry Powdery Mildew'),
          })}
        </span>
        <PowderyMildewTooltip crop={'cherry'} />
      </div>
    ),
    fieldAssetPicker: (
      <DropdownSelectorBlock
        valuesTimeseriesToFilterOn={allPossibleValueTypes}
        propertyPermissionsToCheck={['VIEW_CHERRY_POWDERY_MILDEW_DETAILS']}
        showAssociatedWeatherStationName
      />
    ),
    id: `stackem-cherry-powdery-mildew-${blockId}`,
  }

  if (!hasPermission && selectedProperty) {
    return { ...propertyLacksPermissionSectionMaker(commonReturnItems) }
  }

  const blockHasValueTypes = doesSelectedBlockHaveValueTypes({ valuesTimeseries: [valueTypeKey] })

  if (!blockHasValueTypes || !cherryCropIds.includes(Number(cropIdFromFieldAssets))) {
    return blockLacksCropSectionMaker({
      ...commonReturnItems,
      translatedCropName: translate.phrases.dbCropsName('cherry'),
    })
  }

  const series: TChartSeries[] = []

  let biofixPlotLines: TXAxisPlotLinesOptionsWithTooltipDisplay[] = []

  if (blockHasValueTypes) {
    const cropDegreeDaysCumulativeSeriesForBlock: TChartSeries & { color?: TRGBAColorWith1AtTheEnd } = {
      name: translate.phrases.banyanApp('{{cropName}} Cumulative Degree Days', {
        cropName: getCropNameFromId(Number(cropIdFromFieldAssets)),
      }),
      id: 'cherryPowderyMildew',
      tooltip: {
        valueDecimals: unitConverter.cropDegreeDaysCumulative().defaultNumberOfDecimalPlaces(),
        valueSuffix: ` ${unitConverter.cropDegreeDaysCumulative().suffix()}`,
      },
      yAxis: 0,
      data: (data?.blocks?.[blockId]?.values?.[valueTypeKey]?.[0]?.timeseries ?? []).map(
        (d: { timestamp: string; value: number | null }) => [
          +new Date(d.timestamp),
          unitConverter.cropDegreeDaysCumulative(d.value).value(),
        ],
      ),
      type: 'line',
      marker: {
        enabled: false,
      },
    }

    series.push(cropDegreeDaysCumulativeSeriesForBlock)

    if (compareSeasonsInterval) {
      series.push(
        makeCompareSeasonsSeriesFromRegularSeries(cropDegreeDaysCumulativeSeriesForBlock, {
          data: (compareSeasonsData?.blocks?.[blockId]?.values?.[valueTypeKey]?.[0]?.timeseries ?? []).map(
            (d: { timestamp: string; value: number | null }) => [
              +new Date(d.timestamp),
              unitConverter.cropDegreeDaysCumulative(d.value).value(),
            ],
          ),
        }),
      )
    }
  }

  const stationLngLat = selectedFieldAssets[pointCategory]

  if (stationLngLat) {
    // precipitation might not come daily depending on the precipitation chart also loading.
    // this isn't super ideal but let's do a quick daily aggregation of the rain data if necessary.
    const isPrecipitationDaily =
      data?.points?.[stationLngLat]?.values?.precipitation?.[0]?.metadata?.aggregationInterval === 'DAILY'

    let rainData: Array<[number, number | null]>

    if (isPrecipitationDaily) {
      rainData = (data?.points?.[stationLngLat]?.values?.precipitation?.[0]?.timeseries ?? []).map((d) => [
        +new Date(d.timestamp),
        unitConverter.precipitation(d.value.sum).value(),
      ])
    } else {
      rainData = makeDailyRainForDiseases(
        data?.points?.[stationLngLat]?.values?.precipitation?.[0]?.timeseries ?? [],
        timezone,
      )
    }

    const precipitationSeries: TChartSeriesWithTRGBAColorWith1AtTheEndColor = {
      name: unitConverter.precipitation().shortTitle(),
      id: 'precipitation',
      tooltip: {
        valueDecimals: unitConverter.precipitation().defaultNumberOfDecimalPlaces(),
        valueSuffix: ` ${unitConverter.precipitation().suffix()}`,
      },
      yAxis: 1,
      data: rainData,
      type: 'area',
      step: 'center',
      showInLegend: true,
    }

    series.push(precipitationSeries)

    if (compareSeasonsInterval) {
      let compareSeasonsRainData: Array<[number, number | null]>

      if (isPrecipitationDaily) {
        compareSeasonsRainData = (
          compareSeasonsData?.points?.[stationLngLat]?.values?.precipitation?.[0]?.timeseries ?? []
        ).map((d) => [+new Date(d.timestamp), unitConverter.precipitation(d.value.sum).value()])
      } else {
        compareSeasonsRainData = makeDailyRainForDiseases(
          compareSeasonsData?.points?.[stationLngLat]?.values?.precipitation?.[0]?.timeseries ?? [],
          timezone,
        )
      }

      series.push(
        makeCompareSeasonsSeriesFromRegularSeries(precipitationSeries, {
          data: compareSeasonsRainData,
        }),
      )
    }
  }

  const allForecastMoments = [
    data?.blocks?.[blockId]?.values?.[valueTypeKey]?.[0]?.metadata?.forecastStartsAt,
  ]
    .filter(Boolean)
    .map((d) => moment.tz(d, timezone))

  const firstForecastTimestamp = allForecastMoments.length ? +moment.min(allForecastMoments) : +new Date()

  return {
    ...commonReturnItems,
    items: [
      {
        chartConfig: {
          semiosHighchartsAdditions: {
            id: 'cherryPowderyMildew',
            firstForecastTimestamp,
          },
          chart: {
            type: 'line',
          },
          tooltip: {
            xDateFormat: getXDateFormat(true),
          },
          xAxis: {
            plotLines: biofixPlotLines,
          },
          yAxis: [
            {
              title: {
                text: translate.phrases.banyanApp('Degree Days'),
                x: 5,
              },
              opposite: false,
              gridLineWidth: 1,
              allowDecimals: false,
              offset: -12,
            },
            {
              title: {
                text: translate.phrases.banyanApp('Rain'),
              },
              opposite: true,
              softMax: 1,
            },
          ],
          series,
        },
      },
    ],
  }
}
